import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';

import {
  Box,
  FormGroup,
  IconButton,
  Slider,
  Tooltip,
  Typography,
} from '@mui/material';

import PowerIcon from '@mui/icons-material/Power';
import WhatshotIcon from '@mui/icons-material/Whatshot';

import {
  setPowerOutagesMinDeviation,
  setPowerOutagesMinDuration,
  setTemperatureBreachesMinDeviation,
  setTemperatureBreachesMinDuration,
  store,
} from '../../store';

export const TemperatureAndPowerWidget = ({
  temperatureBreachCount,
  powerOutageCount,
}: {
  temperatureBreachCount: number;
  powerOutageCount: number;
}) => {
  const {
    minDeviation: powerOutageMinDeviation,
    minDuration: powerOutageMinDuration,
  } = useSnapshot(store.powerOutages);
  const { t } = useTranslation();
  const { minDeviation: tempMinDeviation, minDuration: tempMinDuration } =
    useSnapshot(store.temperatureBreaches);

  const [showSliders, setShowSliders] = useState<boolean>(false);

  const temperatureDescription = t(
    'reeferInsights.temperatureAndPower.breachDescription',
    {
      minDeviation: tempMinDeviation,
      minDuration: tempMinDuration,
    },
  );
  const powerDescription = t(
    'reeferInsights.temperatureAndPower.powerOutageDescription',
    {
      minDeviation: powerOutageMinDeviation,
      minDuration: powerOutageMinDuration,
    },
  );

  return (
    <Box
      sx={{
        borderRadius: '20px',
        padding: '20px',
        border: '1px solid #ccc',
        position: 'relative',
        backgroundColor: '#fff',
      }}
    >
      {/* Header */}

      {/* Temperature Breaches */}
      <Box marginBottom={0}>
        <Tooltip title={temperatureDescription}>
          <Box>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              {t('reeferInsights.temperatureAndPower.breaches')}
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <WhatshotIcon color="warning" />
              <Typography variant="h4" color="warning">
                {temperatureBreachCount}
              </Typography>
            </Box>
          </Box>
        </Tooltip>

        {showSliders && (
          <FormGroup sx={{ marginTop: 2 }}>
            <Typography variant="body2" gutterBottom>
              Minimum Deviation (°C)
            </Typography>
            <Slider
              value={tempMinDeviation}
              onChange={(_, value) =>
                setTemperatureBreachesMinDeviation(value as number)
              }
              min={3}
              max={20}
              step={1}
              valueLabelDisplay="auto"
            />
            <Typography variant="body2" gutterBottom>
              Minimum Duration (hours)
            </Typography>
            <Slider
              value={tempMinDuration}
              onChange={(_, value) =>
                setTemperatureBreachesMinDuration(value as number)
              }
              min={1}
              max={24}
              step={1}
              valueLabelDisplay="auto"
            />
          </FormGroup>
        )}
      </Box>

      {/* Power Outages */}
      <Box>
        <Tooltip title={powerDescription}>
          <Box>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              {t('reeferInsights.temperatureAndPower.powerOutages')}
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <PowerIcon color="error" />
              <Typography variant="h4" color="error">
                {powerOutageCount}
              </Typography>
            </Box>
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
};
